import type { BagEvent, PairEvent, SpinnerEvent } from '../probabilityEvents.ts';
import type { CounterColour } from '../../../MathFactsGenerator/Constants.ts';

/**
 * French probability prompts (ADR 0108 §2). Rouge, bleu, vert and jaune give
 * R, B, V, J: no two share an initial.
 */
const COLOURS: Record<CounterColour, { letter: string; word: string }> = {
    R: { letter: 'R', word: 'rouge' },
    B: { letter: 'B', word: 'bleu' },
    G: { letter: 'V', word: 'vert' },
    Y: { letter: 'J', word: 'jaune' },
};

function spinnerEvent(event: SpinnerEvent): string {
    switch (event.kind) {
        case 'number':
            return `d'obtenir un ${event.n}`;
        case 'not':
            return `de ne pas obtenir un ${event.n}`;
        case 'even':
            return `d'obtenir un nombre pair`;
        case 'odd':
            return `d'obtenir un nombre impair`;
        case 'greater':
            return `d'obtenir un nombre supérieur à ${event.n}`;
        case 'less':
            return `d'obtenir un nombre inférieur à ${event.n}`;
    }
}

function bagEvent(event: BagEvent): string {
    switch (event.kind) {
        case 'colour':
            return `qu'il soit ${COLOURS[event.colour].word}`;
        case 'not':
            return `qu'il ne soit pas ${COLOURS[event.colour].word}`;
        case 'either':
            return `qu'il soit ${COLOURS[event.colours[0]].word} ou ${COLOURS[event.colours[1]].word}`;
    }
}

function pairEvent(event: PairEvent): string {
    switch (event.kind) {
        case 'both':
            return `d'obtenir un ${event.left} à gauche et un ${event.right} à droite`;
        case 'bothEven':
            return `d'obtenir deux nombres pairs`;
        case 'same':
            return `d'obtenir le même nombre sur les deux`;
    }
}

export const probabilityPrompts = {
    instruction: () => `Écris chaque probabilité sous la forme d'une fraction irréductible.`,
    colours: COLOURS,
    counterKey: () =>
        (Object.keys(COLOURS) as CounterColour[]).map((c) => `${COLOURS[c].letter} = ${COLOURS[c].word}`).join(', '),
    oneSpinner: (event: SpinnerEvent) =>
        `Fais tourner la roulette une fois. Quelle est la probabilité ${spinnerEvent(event)} ?`,
    bag: (event: BagEvent) => `Prends un jeton sans regarder. Quelle est la probabilité ${bagEvent(event)} ?`,
    twoSpinners: (event: PairEvent) =>
        `Fais tourner les deux roulettes. Quelle est la probabilité ${pairEvent(event)} ?`,
    outcomes: () => `Fais tourner les deux roulettes. Combien de résultats différents y a-t-il ?`,
};
